import _ from 'lodash';

const minRatio = 0.2;
const maxRatio = 1;

const barRatio = (
  price: number,
  minPrice: number,
  maxPrice: number,
  samePriceAcross: boolean,
  middle: number,
) => {
  let ratio;

  if (minPrice <= 0 || price === null) {
    ratio = 0;
  } else if (samePriceAcross || price === middle) {
    ratio = (minRatio + maxRatio) / 2;
  } else if (price <= minPrice) {
    ratio = minRatio;
  } else if (price < maxPrice) {
    ratio =
      minRatio +
      ((price - minPrice) / (maxPrice - minPrice)) * (maxRatio - minRatio);
  } else if (price === maxPrice) {
    ratio = maxRatio;
  } else if (price > maxPrice) {
    ratio = Number.POSITIVE_INFINITY;
  } else {
    ratio = 0;
  }
  return ratio;
};

const outlierLimit = (prices: number[]) => {
  if (!prices) {
    return Number.MAX_VALUE;
  }
  if (prices.length <= 4) {
    return Number.MAX_VALUE;
  }

  const sortedPrices = _.sortBy(prices);
  const quarterI = Math.floor(sortedPrices.length / 4);
  const quartiles = [
    sortedPrices[quarterI],
    sortedPrices[2 * quarterI],
    sortedPrices[3 * quarterI],
  ];
  return quartiles[2] + 2 * (quartiles[2] - quartiles[0]);
};

const pricesToSanitizedArray = (validPrices: number[]) => {
  const outlierLimitVal = outlierLimit(validPrices);
  return _.reject(validPrices, (p) => p > outlierLimitVal);
};

const mean = (prices: number[]) =>
  prices.length > 0
    ? _.reduce(prices, (sum, num) => sum + num, 0) / prices.length
    : NaN;

const stdDeviation = (prices: number[]) => {
  const meanVal = mean(prices);

  let squareSum = 0;
  for (let i = 0; i < prices.length; i += 1) {
    const val = (prices[i] - meanVal) ** 2;

    squareSum += val;
  }

  return Math.sqrt(squareSum / prices.length);
};

const cloud = (
  maxPrice: number,
  minPrice: number,
  meanVal: number,
  stdDeviationVal: number,
) => {
  let cloudVal = Math.ceil(meanVal + stdDeviationVal * 3);

  if (maxPrice > cloudVal) {
    if (Math.abs(maxPrice / cloudVal) <= 1.2) {
      cloudVal = maxPrice;
    }
  } else {
    cloudVal = maxPrice;
  }

  if (cloudVal - minPrice === 1) {
    cloudVal += 1;
  }
  if (cloudVal === minPrice && maxPrice !== minPrice) {
    cloudVal = maxPrice;
  }

  return cloudVal;
};

const defaultResult = {
  barHeights: [],
  scaleCoordinates: [],
};

const getScaleCoordinates = (prices: number[]) => {
  const uniqPrices = _.uniq(prices);
  const minPrice = Math.min.apply(null, uniqPrices);
  const maxPrice = Math.max.apply(null, uniqPrices);

  const coords = uniqPrices.map((price) => {
    const value =
      maxPrice === minPrice ? 0.5 : (price - minPrice) / (maxPrice - minPrice);
    const proportion = minRatio + value * (maxRatio - minRatio);
    return {
      proportion: parseFloat(proportion.toFixed(3)),
      value: price,
    };
  });
  return coords;
};

const getHeightsAndScaleValues = (
  dailyPrices?: Array<number | null> | null,
) => {
  if (!dailyPrices) {
    return defaultResult;
  }

  let validPrices = _.compact(dailyPrices);
  validPrices = validPrices.map((price) => Math.ceil(price));

  const maxPrice = Math.max.apply(null, validPrices);
  const minPrice = Math.min.apply(null, validPrices);

  const pricesWithNoOutliers = pricesToSanitizedArray(validPrices);

  const meanVal = mean(pricesWithNoOutliers);
  const stdDeviationVal = stdDeviation(pricesWithNoOutliers);
  const cloudVal = cloud(maxPrice, minPrice, meanVal, stdDeviationVal);
  const middle = (minPrice + cloudVal) / 2;
  const samePriceAcross = minPrice === middle && middle === cloudVal;

  const barHeights: Array<number | null> = [];
  dailyPrices.forEach((dayData) => {
    const price = dayData;
    let barHeight = null;
    if (price) {
      barHeight = parseFloat(
        barRatio(
          Math.ceil(price),
          minPrice,
          cloudVal,
          samePriceAcross,
          middle,
        ).toFixed(3),
      );
    }
    barHeights.push(barHeight);
  });

  let scalePrices: number[] = [];
  if (validPrices.length) {
    scalePrices = _.compact([minPrice, Math.ceil(middle), Math.ceil(cloudVal)]);
  }

  return {
    barHeights,
    scaleCoordinates: getScaleCoordinates(scalePrices),
  };
};

export default getHeightsAndScaleValues;
